// Central color theme system for Arrow AI with light/dark mode support.

const createTintedMode = ({
  background,
  panel,
  card,
  accent,
  accentHover,
  text,
  secondaryText,
  mutedText,
  border,
  strongBorder,
  active,
  isDark = false,
}) => ({
  'app-background': background,
  'panel-background': panel,
  'card-background': card,

  'chat-bubble-user': accent,
  'chat-bubble-user-text': '#ffffff',
  'chat-bubble-assistant': card,
  'message-input-background': card,
  'message-input-action-button': card,
  'message-input-action-button-hover': active,
  'message-input-border': border,
  'message-input-focus-border': accent,
  'typing-indicator': mutedText,

  'sidebar-background': panel,
  'sidebar-item-hover': card,
  'sidebar-item-active': active,

  'text-primary': text,
  'text-secondary': secondaryText,
  'text-muted': mutedText,
  'text-on-accent': '#ffffff',

  'border-subtle': border,
  'border-strong': strongBorder,
  'button-primary': accent,
  'button-primary-hover': accentHover,
  'button-secondary': card,
  'button-secondary-hover': active,

  'model-card-background': background,
  'model-card-hover': card,
  'model-card-active': active,

  'success': isDark ? '#4ade80' : '#15803d',
  'warning': isDark ? '#facc15' : '#b45309',
  'error': isDark ? '#f87171' : '#dc2626',
  'info': isDark ? '#60a5fa' : '#2563eb',

  'code-background': card,
  'code-border': border,
  'code-text': text,

  'scrollbar-track': card,
  'scrollbar-thumb': strongBorder,
  'scrollbar-thumb-hover': mutedText,
});

export const colorThemes = {
  slate: {
    light: {
      // Main backgrounds
      'app-background': 'rgb(255, 255, 255)',
      'panel-background': 'hsl(210, 40%, 98%)',
      'card-background': 'rgb(241, 241, 241)',
      
      // Chat specific colors
      'chat-bubble-user': 'rgb(0, 0, 0)',
      'chat-bubble-user-text': 'rgb(255, 255, 255)',
      'chat-bubble-assistant': 'hsl(210, 20%, 92%)',
      'message-input-background': 'rgb(255, 255, 255)',
      'message-input-action-button': 'rgb(230, 232, 234)',
      'message-input-action-button-hover': 'rgb(194, 194, 194)',
      'message-input-border': 'hsl(215, 16%, 89%)',
      'message-input-focus-border': 'hsl(215, 84%, 70%)',
      'typing-indicator': 'rgb(144, 144, 144)',

      // Sidebar colors
      'sidebar-background': 'rgb(239, 239, 235, 0.2)',
      'sidebar-item-hover': 'rgb(229, 229, 229)',
      'sidebar-item-active': 'rgb(209, 209, 209)',
      
      // Text colors
      'text-primary': 'hsl(222, 84%, 5%)',
      'text-secondary': 'hsl(215, 25%, 27%)',
      'text-muted': 'hsl(215, 13%, 47%)',
      'text-on-accent': 'hsl(210, 40%, 98%)',
      
      // UI Elements
      'border-subtle': 'hsl(215, 16%, 89%)',
      'border-strong': 'hsl(215, 16%, 82%)',
      'button-primary': 'hsl(215, 70%, 55%)',
      'button-primary-hover': 'hsl(215, 75.90%, 67.50%)',
      'button-secondary': 'hsl(210, 20%, 90%)',
      'button-secondary-hover': 'hsl(215, 16%, 85%)',
      
      //Model Dropdown colors
      'model-card-background': 'rgb(255, 255, 255)',
      'model-card-hover': 'rgb(236, 236, 236)',
      'model-card-active': 'rgb(224,224,220)',

      // Status colors
      'success': 'hsl(142, 71%, 45%)',
      'warning': 'hsl(38, 92%, 50%)',
      'error': 'hsl(0, 84%, 60%)',
      'info': 'hsl(199, 89%, 48%)',
      
      // Code syntax highlighting
      'code-background': 'rgb(236, 236, 236)',
      'code-border': 'hsl(215, 16%, 89%)',
      'code-text': 'rgb(12, 12, 12)',
      
      // Scroll bars
      'scrollbar-track': 'hsl(215, 16%, 89%)',
      'scrollbar-thumb': 'hsl(215, 16%, 70%)',
      'scrollbar-thumb-hover': 'hsl(215, 16%, 60%)',
    },
    dark: {
      // Main backgrounds
      'app-background': 'rgb(29,29,29)',
      'panel-background': 'rgb(27,27,27)',
      'card-background': 'rgb(26,26,26)',
      
      // Chat specific colors
      'chat-bubble-user': 'hsl(0, 0.00%, 18.40%)',
      'chat-bubble-user-text': 'rgb(255, 255, 255)',
      'chat-bubble-assistant': 'rgb(26, 26, 26)',
      'message-input-background': 'rgb(36, 36, 36, 0.5)',
      'message-input-action-button': 'rgb(52, 51, 51)',
      'message-input-action-button-hover': 'rgb(42, 41, 41)',
      'message-input-border': 'hsl(50, 8%, 20%)',
      'message-input-focus-border': 'hsl(34, 60%, 45%)',
      'typing-indicator': 'rgb(130, 130, 130)',

      // Sidebar colors
      'sidebar-background': 'rgb(15, 15, 15, 0.4)',
      'sidebar-item-hover': 'rgb(46,46,46)',
      'sidebar-item-active': 'rgb(38,38,38)',
      
      // Text colors
      'text-primary': 'hsl(50, 15%, 90%)',
      'text-secondary': 'hsl(50, 8%, 70%)',
      'text-muted': 'hsl(50, 4%, 50%)',
      'text-on-accent': 'hsl(50, 20%, 10%)',
      
      // UI Elements
      'border-subtle': 'hsl(50, 8%, 20%)',
      'border-strong': 'hsl(50, 8%, 30%)',
      'button-primary': 'hsl(215, 70%, 55%)',
      'button-primary-hover': 'hsl(215, 75.90%, 67.50%)',
      'button-secondary': 'hsl(50, 4%, 18%)',
      'button-secondary-hover': 'hsl(50, 8%, 25%)',

      //Model Dropdown colors
      'model-card-background': 'rgb(29,29,29)',
      'model-card-hover': 'rgb(45,45,45)',
      'model-card-active': 'rgb(45,45,45)',

      // Status colors
      'success': 'hsl(120, 40%, 55%)',
      'warning': 'hsl(45, 75%, 65%)',
      'error': 'hsl(0, 60%, 70%)',
      'info': 'hsl(200, 60%, 70%)',
      
      // Code syntax highlighting
      'code-background': 'rgb(66, 66, 66)',
      'code-border': 'hsl(50, 8%, 20%)',
      'code-text': 'hsl(0, 0.00%, 100.00%)',
      
      // Scroll bars
      'scrollbar-track': 'hsl(50, 8%, 15%)',
      'scrollbar-thumb': 'hsl(50, 8%, 40%)',
      'scrollbar-thumb-hover': 'hsl(50, 8%, 50%)',
    }
  },
  blue: {
    light: createTintedMode({
      background: '#E5F0FF',
      panel: '#C5D5EB',
      card: '#D7E8FF',
      accent: '#1E5AA8',
      accentHover: '#174982',
      text: '#0D1B2A',
      secondaryText: '#29435F',
      mutedText: '#56708B',
      border: '#B7CAE3',
      strongBorder: '#91ACCE',
      active: '#ABC3E1',
    }),
    dark: createTintedMode({
      background: '#0D1523',
      panel: '#111D30',
      card: '#172944',
      accent: '#245EAD',
      accentHover: '#2F73D4',
      text: '#F3F7FF',
      secondaryText: '#C5D5EB',
      mutedText: '#8CA4C4',
      border: '#243853',
      strongBorder: '#355278',
      active: '#243853',
      isDark: true,
    }),
  },
  terminal: {
    light: createTintedMode({
      background: '#E8F3E6',
      panel: '#E5F0E4',
      card: '#D1E7D0',
      accent: '#166534',
      accentHover: '#0F4D27',
      text: '#07180C',
      secondaryText: '#21452B',
      mutedText: '#52725A',
      border: '#BDD5BE',
      strongBorder: '#8DB292',
      active: '#B6D3B8',
    }),
    dark: createTintedMode({
      background: '#07100A',
      panel: '#0B180F',
      card: '#12301B',
      accent: '#126332',
      accentHover: '#178043',
      text: '#E8F8EA',
      secondaryText: '#AFDAB8',
      mutedText: '#6FA67C',
      border: '#1B4126',
      strongBorder: '#286039',
      active: '#1B4126',
      isDark: true,
    }),
  },
  sunset: {
    light: createTintedMode({
      background: '#FFF0E2',
      panel: '#FCECDF',
      card: '#F7D8C5',
      accent: '#9F3E2C',
      accentHover: '#7E3022',
      text: '#2B1510',
      secondaryText: '#61372D',
      mutedText: '#8A6258',
      border: '#EAC5B0',
      strongBorder: '#CD9D84',
      active: '#E8BEA8',
    }),
    dark: createTintedMode({
      background: '#1D120E',
      panel: '#291811',
      card: '#3A2118',
      accent: '#A64732',
      accentHover: '#C15A42',
      text: '#FFF5EE',
      secondaryText: '#EDC6B7',
      mutedText: '#B98978',
      border: '#513126',
      strongBorder: '#714536',
      active: '#513126',
      isDark: true,
    }),
  },
  floral: {
    light: {
      // Main backgrounds – subtle light pinks
      'app-background': 'rgb(252, 243, 246)',
      'panel-background': 'hsl(340, 45%, 95%)',
      'card-background': 'hsl(340, 45%, 96%)',
      
      // Chat specific colors
      'chat-bubble-user': 'hsl(339, 100.00%, 94.90%)',
      'chat-bubble-user-text': 'hsl(330, 30%, 15%)',
      'chat-bubble-assistant': 'hsl(340, 45%, 93%)',
      'message-input-background': 'rgb(251, 231, 243)',
      'message-input-action-button': 'rgb(235, 203, 222)',
      'message-input-action-button-hover': 'rgb(223, 166, 205)',
      'message-input-border': 'hsl(340, 30%, 85%)',
      'message-input-focus-border': 'hsl(340, 70%, 70%)',
      'typing-indicator': 'rgb(48, 27, 35)',

      // Sidebar colors
      'sidebar-background': 'hsla(340, 45%, 94%, 0.6)',
      'sidebar-item-hover': 'hsl(340, 30%, 92%)',
      'sidebar-item-active': 'hsl(340, 30%, 90%)',
      
      // Text colors
      'text-primary': 'hsl(330, 30%, 20%)',
      'text-secondary': 'hsl(330, 20%, 35%)',
      'text-muted': 'hsl(330, 15%, 50%)',
      'text-on-accent': 'hsl(330, 30%, 12%)',
      
      // UI Elements
      'border-subtle': 'hsl(340, 30%, 88%)',
      'border-strong': 'hsl(340, 25%, 80%)',
      'button-primary': 'hsl(215, 70%, 55%)',
      'button-primary-hover': 'hsl(215, 75.90%, 67.50%)',
      'button-secondary': 'hsl(340, 45%, 94%)',
      'button-secondary-hover': 'hsl(340, 35%, 90%)',
      
      //Model Dropdown colors
      'model-card-background': 'rgb(240, 220, 232)',
      'model-card-hover': 'rgb(237, 217, 229)',
      'model-card-active': 'rgb(45,45,45)',

      // Status colors
      'success': 'hsl(120, 50%, 45%)',
      'warning': 'hsl(45, 85%, 55%)',
      'error': 'hsl(0, 70%, 60%)',
      'info': 'hsl(200, 70%, 60%)',
      
      // Code syntax highlighting
      'code-background': 'rgb(252, 232, 239)',
      'code-border': 'hsl(340, 30%, 88%)',
      'code-text': 'rgb(330, 30%, 20%)',
      
      // Scroll bars
      'scrollbar-track': 'hsl(340, 30%, 90%)',
      'scrollbar-thumb': 'hsl(340, 25%, 75%)',
      'scrollbar-thumb-hover': 'hsl(340, 25%, 65%)',
    },
    dark: {
      // Main backgrounds – deep, dark purples
      'app-background': 'rgb(25, 18, 33)',
      'panel-background': 'hsl(270, 25%, 12%)',
      'card-background': 'hsl(270, 22%, 14%)',
      
      // Chat specific colors
      'chat-bubble-user': 'hsl(270, 22%, 18%)',
      'chat-bubble-user-text': 'hsl(0, 0%, 100%)',
      'chat-bubble-assistant': 'hsl(270, 20%, 16%)',
      'message-input-background': 'rgba(46, 36, 56, 0.6)',
      'message-input-action-button': 'rgb(55, 42, 67)',
      'message-input-action-button-hover': 'rgb(33, 25, 41)',
      'message-input-border': 'hsl(270, 20%, 22%)',
      'message-input-focus-border': 'hsl(270, 60%, 55%)',
      'typing-indicator': 'rgb(59, 48, 74)',

      // Sidebar colors
      'sidebar-background': 'hsla(270, 25%, 10%, 0.6)',
      'sidebar-item-hover': 'hsl(270, 20%, 22%)',
      'sidebar-item-active': 'hsl(270, 20%, 18%)',
      
      // Text colors
      'text-primary': 'hsl(270, 60%, 90%)',
      'text-secondary': 'hsl(270, 30%, 75%)',
      'text-muted': 'hsl(270, 20%, 60%)',
      'text-on-accent': 'hsl(0, 0%, 100%)',
      
      // UI Elements
      'border-subtle': 'hsl(270, 20%, 22%)',
      'border-strong': 'hsl(270, 20%, 30%)',
      'button-primary': 'hsl(215, 70%, 55%)',
      'button-primary-hover': 'hsl(215, 75.90%, 67.50%)',
      'button-secondary': 'hsl(270, 22%, 18%)',
      'button-secondary-hover': 'hsl(270, 22%, 24%)',
      
      //Model Dropdown colors
      'model-card-background': 'rgb(32, 25, 39)',
      'model-card-hover': 'rgb(22, 15, 29)',
      'model-card-active': 'rgb(45,45,45)',
      
      // Status colors
      'success': 'hsl(120, 40%, 55%)',
      'warning': 'hsl(45, 75%, 65%)',
      'error': 'hsl(0, 60%, 70%)',
      'info': 'hsl(200, 60%, 70%)',
      
      // Code syntax highlighting
      'code-background': 'rgb(47, 35, 63)',
      'code-border': 'hsl(270, 20%, 22%)',
      'code-text': 'hsl(269, 72.40%, 94.30%)',
      
      // Scroll bars
      'scrollbar-track': 'hsl(270, 20%, 14%)',
      'scrollbar-thumb': 'hsl(270, 20%, 32%)',
      'scrollbar-thumb-hover': 'hsl(270, 20%, 40%)',
    }
  }
};

export const DEFAULT_COLOR_THEME = 'slate';

export const colorThemeOptions = [
  { id: 'slate', label: 'Slate' },
  { id: 'floral', label: 'Floral' },
  { id: 'blue', label: 'Blue' },
  { id: 'terminal', label: 'Terminal' },
  { id: 'sunset', label: 'Sunset' },
];

export const normalizeColorTheme = (themeName) =>
  Object.prototype.hasOwnProperty.call(colorThemes, themeName)
    ? themeName
    : DEFAULT_COLOR_THEME;

// Helper function to parse any color format to RGB
const parseColor = (color) => {
  // Handle rgb/rgba format
  const rgbMatch = color.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
  if (rgbMatch) {
    return { r: parseInt(rgbMatch[1]), g: parseInt(rgbMatch[2]), b: parseInt(rgbMatch[3]) };
  }

  // Handle hsl/hsla format
  const hslMatch = color.match(/hsla?\((\d+),\s*([\d.]+)%?,\s*([\d.]+)%?/);
  if (hslMatch) {
    const h = parseInt(hslMatch[1]) / 360;
    const s = parseFloat(hslMatch[2]) / 100;
    const l = parseFloat(hslMatch[3]) / 100;

    // Convert HSL to RGB
    let r, g, b;
    if (s === 0) {
      r = g = b = l;
    } else {
      const hue2rgb = (p, q, t) => {
        if (t < 0) t += 1;
        if (t > 1) t -= 1;
        if (t < 1/6) return p + (q - p) * 6 * t;
        if (t < 1/2) return q;
        if (t < 2/3) return p + (q - p) * (2/3 - t) * 6;
        return p;
      };
      const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
      const p = 2 * l - q;
      r = hue2rgb(p, q, h + 1/3);
      g = hue2rgb(p, q, h);
      b = hue2rgb(p, q, h - 1/3);
    }
    return { r: Math.round(r * 255), g: Math.round(g * 255), b: Math.round(b * 255) };
  }

  // Handle hex format
  const hexMatch = color.match(/^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i);
  if (hexMatch) {
    return { r: parseInt(hexMatch[1], 16), g: parseInt(hexMatch[2], 16), b: parseInt(hexMatch[3], 16) };
  }

  return null;
};

// Helper function to adjust color brightness
const adjustBrightness = (color, amount) => {
  const rgb = parseColor(color);
  if (!rgb) return color;

  const adjust = (value) => Math.max(0, Math.min(255, value + amount));
  return `rgb(${adjust(rgb.r)}, ${adjust(rgb.g)}, ${adjust(rgb.b)})`;
};

// Helper function to get theme colors
export const getThemeColors = (themeName = DEFAULT_COLOR_THEME, mode = 'light') => {
  const resolvedTheme = normalizeColorTheme(themeName);
  const resolvedMode = mode === 'dark' ? 'dark' : 'light';
  return colorThemes[resolvedTheme][resolvedMode];
};

// Helper function to apply theme colors to CSS custom properties
export const applyThemeColors = (themeName = DEFAULT_COLOR_THEME, mode = 'light') => {
  const resolvedTheme = normalizeColorTheme(themeName);
  const colors = getThemeColors(resolvedTheme, mode);
  const root = document.documentElement;

  Object.entries(colors).forEach(([key, value]) => {
    root.style.setProperty(`--color-${key}`, value);
  });

  // Derive model selected-row highlight from theme accent color.
  // `message-input-focus-border` varies by color theme,
  // while `button-primary` is a safe fallback.
  const selectionBaseColor = colors['message-input-focus-border'] || colors['button-primary'];
  const selectionRgb = parseColor(selectionBaseColor) || parseColor(colors['button-primary']);
  if (selectionRgb) {
    const activeBgAlpha = mode === 'light' ? 0.18 : 0.32;
    const activeHoverAlpha = mode === 'light' ? 0.24 : 0.38;
    const activeBorderAlpha = mode === 'light' ? 0.44 : 0.62;
    root.style.setProperty(
      '--color-model-card-active',
      `rgba(${selectionRgb.r}, ${selectionRgb.g}, ${selectionRgb.b}, ${activeBgAlpha})`
    );
    root.style.setProperty(
      '--color-model-card-active-hover',
      `rgba(${selectionRgb.r}, ${selectionRgb.g}, ${selectionRgb.b}, ${activeHoverAlpha})`
    );
    root.style.setProperty(
      '--color-model-card-active-border',
      `rgba(${selectionRgb.r}, ${selectionRgb.g}, ${selectionRgb.b}, ${activeBorderAlpha})`
    );
  } else {
    root.style.setProperty('--color-model-card-active-hover', colors['model-card-active']);
    root.style.setProperty('--color-model-card-active-border', selectionBaseColor);
  }

  // Derive message-input-background from app-background unless a theme has an
  // intentional override.
  const appBg = colors['app-background'];
  const inputAdjustment = mode === 'light' ? -15 : 15;
  const derivedInputBg = adjustBrightness(appBg, inputAdjustment);
  const messageInputBg =
    resolvedTheme === DEFAULT_COLOR_THEME && mode === 'light'
      ? colors['message-input-background']
      : derivedInputBg;
  root.style.setProperty('--color-message-input-background', messageInputBg);

  // Match input border to input background.
  root.style.setProperty('--color-message-input-border', messageInputBg);

  // Derive disabled send button color from message-input-background
  // Slightly lighter/darker than the input background
  const buttonAdjustment = mode === 'light' ? -20 : 20;
  const derivedDisabledButton = adjustBrightness(messageInputBg, buttonAdjustment);
  root.style.setProperty('--color-send-button-disabled', derivedDisabledButton);
};

// CSS custom properties generator
export const generateCSSVariables = (themeName = DEFAULT_COLOR_THEME, mode = 'light') => {
  const colors = getThemeColors(themeName, mode);
  return Object.entries(colors)
    .map(([key, value]) => `  --color-${key}: ${value};`)
    .join('\n');
};
